<template>
  <v-container>
    <div class="ml-2" v-if="salesDate">
      <PageTitle text="Record Card Sales" class="font" />
    </div>

    <div
      v-if="salesDate"
      class="buttonText ml-1 d-flex flex-row flex-grow-1 flex-wrap"
    >
      <div class="buttonText ml-1">
        <v-menu
          v-model="menu.edit"
          :close-on-content-click="false"
          transition="scale-transition"
          :nudge-left="10"
          offset-y
          min-width="auto"
          class="ft font-weight-medium"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="font mt-2 ml-2 font-weight-medium"
              outlined
              v-bind="attrs"
              :disabled="isPageLoading || isLoading"
              v-on="on"
              min-width="150px"
            >
              <v-icon left small>event</v-icon>
              <span class="ft">{{
                salesDate ? salesDate : "Choose A Date"
              }}</span>
            </v-btn>
          </template>
          <v-date-picker
            :max="today"
            :allowed-dates="isDateAllowed"
            :events="recordedDates"
            class="font font-weight-medium"
            v-model="salesDate"
            event-color="primary"
            @input="menu.edit = false"
          ></v-date-picker>
        </v-menu>
      </div>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            :loading="isPageLoading"
            icon
            fab
            @click="refreshPage"
            small
            v-on="on"
            color="primary"
            class="mt-2 ml-2"
          >
            <i class="material-icons-outlined">refresh</i>
          </v-btn>
        </template>
        <span class="font font-weight-medium font-size-sm">Refresh Page</span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <div
        v-if="salesDate && !hasRecordedTransactions"
        class="d-flex flex-row mt-3 mr-5 animate__animated animate__fadeIn"
      >
        <v-alert type="success" dense>
          <div class="font font-weight-medium text-uppercase font-size-sm">
            Card Sales Total -
            <span class="font font-weight-bold font-size-md">{{
              totalAmount | currencyFormat
            }}</span>
          </div>
        </v-alert>
      </div>
    </div>

    <v-row
      align="center"
      justify="center"
      style="margin-top: 5px; margin-left: 1px; margin-right: 10px"
    >
      <v-col cols="12">
        <SkeletonPreloader
          v-if="isPageLoading"
          class="d-flex flex-column mt-4"
          type="list-item-two-line"
          :count="6"
        />

        <!--        <EmptyPage-->
        <!--          v-else-if="!salesDate"-->
        <!--          class="mt-5"-->
        <!--          :image="require('@/assets/payment-method.svg')"-->
        <!--          image-width="90px"-->
        <!--          title="Select a sales date"-->
        <!--          sub-title="Card sales fields will load after a recording date is selected."-->
        <!--        />-->
        <template v-else-if="!salesDate">
          <div
            class="d-flex flex-row flex-fill justify-center mt-16 animate__animated animate__fadeIn"
          >
            <img
              :src="require('@/assets/payment-method.svg')"
              style="width: 150px;"
            />
          </div>
          <div class="d-flex flex-column flex-fill align-center mt-10">
            <span
              class="font font-weight-medium pt-2 animate__animated animate_fadeInDown"
            >
              Select A Date To Start Card Sales Recordings
            </span>

            <v-chip
              color="primary"
              label
              small
              class="font font-weight-medium font-size-sm my-2"
              >Instructions to Follow</v-chip
            >
            <span class="font font-weight-medium font-size-sm pt-2"
              >Choose <kbd>yesterday's date</kbd> to close the daily card sales
              transactions</span
            >
            <p>
              <v-menu
                v-model="menu.add"
                :close-on-content-click="false"
                transition="scale-transition"
                :nudge-left="10"
                offset-y
                min-width="auto"
                class="font font-weight-medium  "
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="font mt-4 font-weight-medium"
                    outlined
                    v-bind="attrs"
                    :disabled="isPageLoading || isLoading"
                    v-on="on"
                    min-width="120px"
                  >
                    <span class=" font font-weight-medium">{{
                      salesDate ? salesDate : "Choose A Date"
                    }}</span>
                  </v-btn>
                </template>
                <v-date-picker
                  :min="''"
                  event-color="primary"
                  :events="[]"
                  :max="today | timestamp('YYYY-MM-DD')"
                  class="font font-weight-medium  "
                  v-model="salesDate"
                  @input="menu.add = false"
                ></v-date-picker>
              </v-menu>
            </p>
            <span v-if="isPageLoading || isLoading">
              <v-progress-circular
                indeterminate
                color="primary"
                size="30"
                width="2"
              />
            </span>
          </div>
        </template>

        <v-alert
          v-else-if="hasRecordedTransactions"
          dense
          type="success"
          class="animate__animated animate__shakeX font font-size-sm font-weight-medium mt-4"
        >
          Card transactions for {{ salesDate }} have already been recorded.
        </v-alert>

        <v-alert
          v-else-if="categories.length === 0"
          dense
          type="warning"
          class="font font-size-sm font-weight-medium mt-4"
        >
          No card categories are available for recording.
        </v-alert>

        <ValidationObserver v-else v-slot="{ handleSubmit, invalid }">
          <v-card
            class="font box-shadow-light pt-5 pb-4 animate__animated animate__fadeIn"
            style="border-radius: 8px;"
          >
            <v-form @submit.prevent="handleSubmit(recordCardTransactions)">
              <v-card-text>
                <div
                  class="d-flex flex-row flex-grow-1 justify-space-between align-center flex-wrap mb-3"
                >
                  <div class="font font-weight-bold blue-grey--text">
                    Recording card transactions for
                    <v-chip small label color="primary" class="white--text">{{
                      salesDate
                    }}</v-chip>
                  </div>

                  <v-btn
                    small
                    color="primary"
                    class="font font-weight-medium"
                    @click="addTransaction"
                  >
                    <v-icon left small>add</v-icon>
                    Add Transaction
                  </v-btn>
                </div>

                <v-expansion-panels flat multiple v-model="expandedRows">
                  <v-expansion-panel
                    v-for="(transaction, index) in transactions"
                    :key="transaction.uid"
                    class="box-shadow-light mb-3"
                  >
                    <v-expansion-panel-header>
                      <div
                        class="d-flex flex-row flex-grow-1 justify-space-between align-center"
                      >
                        <span class="font font-weight-bold">
                          Transaction {{ index + 1 }}
                          <v-chip
                            v-if="categoryName(transaction.cardCategoryId)"
                            small
                            label
                            color="primary"
                            class="ml-2 white--text"
                            >{{
                              categoryName(transaction.cardCategoryId)
                            }}</v-chip
                          >
                        </span>
                        <span class="font font-weight-bold font-size-sm">
                          {{ Number(transaction.amount || 0) | currencyFormat }}
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="4">
                          <ValidationProvider
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <v-autocomplete
                              dense
                              outlined
                              item-text="name"
                              item-value="id"
                              :items="categories"
                              v-model="transaction.cardCategoryId"
                              prepend-inner-icon="credit_card"
                              label="Card Type"
                              placeholder="Select Shell, Ecobank, Easypass"
                              class="font font-weight-medium text-sm"
                              :error-messages="errors[0]"
                            />
                          </ValidationProvider>
                        </v-col>
                        <v-col cols="12" md="4">
                          <ValidationProvider
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <v-text-field
                              dense
                              outlined
                              v-model.trim="transaction.recordNo"
                              prepend-inner-icon="confirmation_number"
                              label="Record No."
                              placeholder="e.g. REC-000234"
                              class="font font-weight-medium text-sm"
                              :error-messages="errors[0]"
                            />
                          </ValidationProvider>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            dense
                            outlined
                            v-model.trim="transaction.authNo"
                            prepend-inner-icon="verified_user"
                            label="Auth No."
                            placeholder="Optional authorization number"
                            class="font font-weight-medium text-sm"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <ValidationProvider
                            rules="required|numeric|min_value:0.01"
                            v-slot="{ errors }"
                          >
                            <v-text-field
                              dense
                              outlined
                              type="number"
                              min="0"
                              step="0.01"
                              v-model.number="transaction.amount"
                              prepend-inner-icon="payments"
                              label="Amount"
                              placeholder="0.00"
                              class="font font-weight-medium text-sm"
                              :error-messages="errors[0]"
                            />
                          </ValidationProvider>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            dense
                            outlined
                            type="number"
                            min="0"
                            step="0.01"
                            v-model.number="transaction.discount"
                            prepend-inner-icon="local_offer"
                            label="Discount"
                            placeholder="Optional discount"
                            class="font font-weight-medium text-sm"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            dense
                            outlined
                            v-model.trim="transaction.reference"
                            prepend-inner-icon="receipt"
                            label="Reference"
                            placeholder="Optional bank or system reference"
                            class="font font-weight-medium text-sm"
                          />
                        </v-col>
                      </v-row>

                      <div class="d-flex flex-row justify-end">
                        <v-btn
                          text
                          small
                          color="red"
                          class="font font-weight-medium"
                          :disabled="transactions.length === 1"
                          @click="removeTransaction(index)"
                        >
                          <v-icon left small>delete</v-icon>
                          Remove
                        </v-btn>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>

                <v-divider class="my-4"></v-divider>

                <div class="d-flex flex-row flex-grow-1 flex-wrap">
                  <v-chip
                    v-for="summary in summaries"
                    :key="summary.cardCategoryId"
                    class="mr-2 mb-2 font font-weight-medium"
                    label
                  >
                    {{ summary.name }}:
                    {{ summary.amount | currencyFormat }}
                  </v-chip>
                </div>
              </v-card-text>

              <v-card-actions class="px-4">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  class="font font-weight-medium"
                  :loading="isLoading"
                  :disabled="invalid || isLoading"
                  type="submit"
                >
                  Record Card Sales
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </ValidationObserver>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import PageTitle from "../../components/PageTitle";
import SkeletonPreloader from "../../components/SkeletonPreloader";
import { currencyFormat } from "@/filters/currencyFormat";
import { timestamp } from "@/filters/timestamp";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { min_value, numeric, required } from "vee-validate/dist/rules";

extend("required", {
  ...required,
  message: "This field is required"
});
extend("numeric", {
  ...numeric,
  message: "This field must be a number"
});
extend("min_value", {
  ...min_value,
  message: "Amount must be greater than 0"
});

export default {
  name: "RecordCardTransactions",
  components: {
    PageTitle,
    SkeletonPreloader,
    ValidationProvider,
    ValidationObserver
  },
  data() {
    return {
      menu: {
        add: false,
        edit: false
      },
      today: moment().format("YYYY-MM-DD"),
      salesDate: null,
      expandedRows: [0],
      transactions: [this.emptyTransaction()]
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters.getIsLoading;
    },
    isPageLoading() {
      return this.$store.getters.getIsPageLoading;
    },
    categories() {
      return this.$store.getters["cards/getCategories"];
    },
    recordedDates() {
      return this.$store.getters["cards/getRecordedDates"];
    },
    recordedTransactions() {
      return this.$store.getters["cards/getRecordedTransactions"];
    },
    hasRecordedTransactions() {
      return this.recordedTransactions.length > 0;
    },
    totalAmount() {
      return this.transactions.reduce(
        (total, transaction) => total + Number(transaction.amount || 0),
        0
      );
    },
    summaries() {
      return this.transactions.reduce((result, transaction) => {
        if (!transaction.cardCategoryId) {
          return result;
        }

        const category = this.categories.find(
          item => item.id === transaction.cardCategoryId
        );
        const existing = result.find(
          item => item.cardCategoryId === transaction.cardCategoryId
        );

        if (existing) {
          existing.amount += Number(transaction.amount || 0);
          return result;
        }

        result.push({
          cardCategoryId: transaction.cardCategoryId,
          name: category ? category.name : "Card",
          amount: Number(transaction.amount || 0)
        });
        return result;
      }, []);
    }
  },
  filters: {
    currencyFormat,
    timestamp
  },
  watch: {
    salesDate(value) {
      if (value) {
        this.loadTransactionsForDate(value);
      }
    }
  },
  methods: {
    emptyTransaction() {
      return {
        uid: `${Date.now()}-${Math.random()}`,
        cardCategoryId: "",
        recordNo: "",
        authNo: "",
        amount: "",
        discount: "",
        reference: ""
      };
    },
    addTransaction() {
      this.transactions.push(this.emptyTransaction());
      this.expandedRows = this.transactions.map((_, index) => index);
    },
    removeTransaction(index) {
      this.transactions.splice(index, 1);
      this.expandedRows = this.transactions.map((_, rowIndex) => rowIndex);
    },
    categoryName(cardCategoryId) {
      const category = this.categories.find(item => item.id === cardCategoryId);
      return category ? category.name : "";
    },
    isDateAllowed(date) {
      return !this.recordedDates.includes(date);
    },
    async refreshPage() {
      await this.$store.dispatch("cards/loadRecordedDates");
      if (this.salesDate) {
        await this.loadTransactionsForDate(this.salesDate);
      }
    },
    async loadRecordedDates() {
      await this.$store.dispatch("cards/loadRecordedDates");
    },
    async loadCardCategories() {
      await this.$store.dispatch("cards/loadCardCategories");
    },
    async loadTransactionsForDate(salesDate) {
      const records = await this.$store.dispatch(
        "cards/loadTransactionsForDate",
        salesDate
      );

      if (records.length === 0) {
        await this.loadCardCategories();
        this.transactions = [this.emptyTransaction()];
        this.expandedRows = [0];
      }
    },
    buildPayload() {
      return {
        transactions: this.transactions.map(transaction => {
          const payload = {
            salesDate: this.salesDate,
            cardCategoryId: transaction.cardCategoryId,
            recordNo: transaction.recordNo,
            amount: Number(transaction.amount)
          };

          if (transaction.authNo) {
            payload.authNo = transaction.authNo;
          }
          if (transaction.reference) {
            payload.reference = transaction.reference;
          }
          if (transaction.discount !== "" && transaction.discount !== null) {
            payload.discount = Number(transaction.discount);
          }

          return payload;
        })
      };
    },
    async recordCardTransactions() {
      const result = await this.$store.dispatch(
        "cards/recordCardTransactions",
        this.buildPayload()
      );

      if (result.success) {
        this.transactions = [this.emptyTransaction()];
        this.expandedRows = [0];
        await this.$store.dispatch("cards/loadRecordedDates");
        if (this.salesDate) {
          await this.loadTransactionsForDate(this.salesDate);
        }
      }
    }
  },
  async created() {
    this.$store.dispatch("isPageLoading", true);
    await this.$store.dispatch("cards/loadRecordedDates");
    this.$store.dispatch("isPageLoading", false);
  }
};
</script>

<style scoped>
.v-expansion-panel {
  border-radius: 6px;
}
</style>
